/**
 * useTokenLivePrice
 *
 * Backwards-compatible wrapper over `useLivePrice` that preserves the old
 * API shape used by existing components. Internally it now connects directly
 * from the browser to the exchange public WebSocket (see useLivePrice).
 *
 * The old prop value `'dynamic'` is treated as "auto-discover" (no exchange
 * passed to useLivePrice). `undefined` has the same effect.
 */

import { useMemo } from "react";

import { useLivePrice } from "@/hooks/useLivePrice";
import type { ExchangeCode } from "@/lib/market-data/market-data.types";

/**
 * Supported exchange codes. `'dynamic'` means "pick the first exchange where
 * the token is available" and is resolved client-side.
 */
export type LivePriceExchangeCode =
  | "binance"
  | "bybit"
  | "gate"
  | "kucoin"
  | "asterdex"
  | "hyperliquid"
  | "dynamic";

interface UseTokenLivePriceReturn {
  price: number | null;
  isConnected: boolean;
  isSubscribed: boolean;
  error: string | null;
  lastUpdate: number | null;
}

function resolveExchange(
  exchange: LivePriceExchangeCode | undefined
): ExchangeCode | undefined {
  if (!exchange || exchange === "dynamic") return undefined;
  return exchange;
}

/**
 * Hook to subscribe to live price updates for a token.
 *
 * @param symbol    - Token symbol (e.g., 'BTC', 'BTCUSDT')
 * @param exchange  - Optional exchange code (defaults to `'dynamic'` which
 *                    auto-picks the first exchange that lists the token)
 * @param enabled   - Whether to enable the subscription (for viewport optimisation)
 */
export const useTokenLivePrice = (
  symbol: string | null | undefined,
  exchange: LivePriceExchangeCode = "dynamic",
  enabled: boolean = true
): UseTokenLivePriceReturn => {
  const resolvedExchange = useMemo(() => resolveExchange(exchange), [exchange]);
  const effectiveSymbol = symbol || "";

  const {
    price: priceUpdate,
    availability,
    error,
    isConnecting,
  } = useLivePrice(effectiveSymbol, resolvedExchange, true, enabled && !!symbol);

  const priceNumber = priceUpdate?.markPrice ?? null;
  const lastUpdate = priceUpdate?.timestamp ?? null;

  // The browser-direct implementation has no single shared connection — each
  // hook owns its own WebSocket — so `isConnected` simply reflects whether we
  // have produced a price tick for the caller.
  const isConnected = priceNumber !== null;

  // `isSubscribed` previously meant "the server confirmed our subscription".
  // In the direct-to-exchange model this is true whenever the token is
  // available on the selected exchange and we're past the connecting phase.
  const isSubscribed = Boolean(
    enabled &&
      symbol &&
      (availability?.available || availability?.spotAvailable) &&
      !isConnecting
  );

  return {
    price: priceNumber,
    isConnected,
    isSubscribed,
    error,
    lastUpdate,
  };
};

export default useTokenLivePrice;
